/**
 * Performance overlay for the section 7 scorecard:
 * "Average FPS, worst-case FPS, frame time, and effect settings."
 *
 * Deliberately engine-agnostic - it measures requestAnimationFrame pacing, not
 * PlayCanvas internals - so it still works if a later prototype is built on a
 * different engine and the numbers stay comparable across them.
 *
 * Caveat worth stating when reporting results: this measures how fast frames
 * are being delivered, which is what a person in the room actually perceives.
 * It is not a GPU profiler and will not tell you *why* a frame was slow.
 */

const HIDDEN_TAB_THRESHOLD_MS = 1000;
const DISPLAY_INTERVAL_MS = 250;

/**
 * Frames inside this window after a reset are ignored.
 *
 * The first second of a scene is dominated by shader compilation and texture
 * upload, which produces a single enormous frame. Counting it would make
 * "worst-case FPS" report load cost forever instead of the stalls that actually
 * happen during play. Load cost belongs in the "Loading" row of the scorecard,
 * measured separately.
 */
const WARMUP_MS = 1000;

export interface StatsOverlay {
  element: HTMLElement;
  /** Start a fresh measurement window - call after changing a setting. */
  reset(): void;
  /** Extra line of context, e.g. the current backing buffer size. */
  setDetail(text: string): void;
  destroy(): void;
}

export function createStatsOverlay(): StatsOverlay {
  const element = document.createElement('div');
  element.className = 'stats';
  element.innerHTML = `
    <div class="stats__row"><span>FPS</span><b data-fps>–</b></div>
    <div class="stats__row"><span>avg</span><b data-avg>–</b></div>
    <div class="stats__row"><span>worst</span><b data-worst>–</b></div>
    <div class="stats__row"><span>frame</span><b data-frame>–</b></div>
    <div class="stats__detail" data-detail></div>
    <button class="stats__reset" type="button" title="Start a fresh measurement window">reset</button>
  `;

  const out = {
    fps: element.querySelector('[data-fps]') as HTMLElement,
    avg: element.querySelector('[data-avg]') as HTMLElement,
    worst: element.querySelector('[data-worst]') as HTMLElement,
    frame: element.querySelector('[data-frame]') as HTMLElement,
    detail: element.querySelector('[data-detail]') as HTMLElement,
  };

  // Rolling window, cleared on reset.
  let windowFrames = 0;
  let windowMs = 0;
  let worstFrameMs = 0;

  // Short window for the "live" number, so it reacts rather than crawling.
  let recentFrames = 0;
  let recentMs = 0;

  let warmupRemainingMs = WARMUP_MS;
  let sinceDisplayMs = 0;
  let last = performance.now();
  let first = true;
  let raf = 0;

  const reset = () => {
    windowFrames = 0;
    windowMs = 0;
    worstFrameMs = 0;
    recentFrames = 0;
    recentMs = 0;
    warmupRemainingMs = WARMUP_MS;
    first = true;
  };

  const render = () => {
    const liveFps = recentMs > 0 ? (recentFrames * 1000) / recentMs : 0;
    const avgFps = windowMs > 0 ? (windowFrames * 1000) / windowMs : 0;
    const worstFps = worstFrameMs > 0 ? 1000 / worstFrameMs : 0;
    const avgFrameMs = windowFrames > 0 ? windowMs / windowFrames : 0;

    out.fps.textContent = liveFps.toFixed(0);
    out.avg.textContent = avgFps.toFixed(0);
    out.worst.textContent = worstFps.toFixed(0);
    out.frame.textContent = `${avgFrameMs.toFixed(1)} ms`;

    // 30 FPS is the plan's stated floor for demanding scenes, 60 preferred.
    element.dataset['grade'] =
      avgFps === 0 ? 'unknown' : avgFps >= 55 ? 'good' : avgFps >= 29 ? 'ok' : 'bad';
  };

  const tick = (now: number) => {
    raf = requestAnimationFrame(tick);

    const dt = now - last;
    last = now;

    // Ignore the first frame and any gap caused by a backgrounded tab, which
    // would otherwise poison the worst-case number.
    if (first || dt > HIDDEN_TAB_THRESHOLD_MS) {
      first = false;
      return;
    }

    if (warmupRemainingMs > 0) {
      warmupRemainingMs -= dt;
      return;
    }

    windowFrames++;
    windowMs += dt;
    recentFrames++;
    recentMs += dt;
    if (dt > worstFrameMs) worstFrameMs = dt;

    // Decay the live window so it reflects roughly the last half second.
    if (recentMs > 500) {
      recentMs *= 0.5;
      recentFrames *= 0.5;
    }

    sinceDisplayMs += dt;
    if (sinceDisplayMs >= DISPLAY_INTERVAL_MS) {
      sinceDisplayMs = 0;
      render();
    }
  };

  raf = requestAnimationFrame(tick);

  (element.querySelector('.stats__reset') as HTMLElement).addEventListener('click', () => {
    reset();
    render();
  });

  return {
    element,
    reset,
    setDetail(text) {
      out.detail.textContent = text;
    },
    destroy() {
      cancelAnimationFrame(raf);
      element.remove();
    },
  };
}
